import React, { useEffect, useState } from 'react';
import { GoBackBtn } from '../../header/GoBackBtn';
import { Dropdown, Toggle } from 'rsuite';
import clsx from 'clsx';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import './MerchantLayout.scss';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import { BUTTON_TYPE, ButtonComponent } from '../../ButtonComponent';
import { MERCHANTS_TABS } from '../../../utils/merchantUtils';
import { checkRoles, CLAIMS } from '../../../utils/roles-helper';
import { IAuthState } from '../../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../../setup';
import { IModalWindow, ModalWindow } from '../../modal/ModalWindow';
import { ApplicationDetailActivityLogsModal } from '../../application/ApplicationDetailActivityLogsModal';

const navigationLinks = [
	{
		path: MERCHANTS_TABS.BUSINESS,
		text: 'MERCHANT.BUSINESS_INFO',
		additionalClass: 'd-flex',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.ADDRESS,
		text: 'MERCHANT.ADDRESS',
		additionalClass: 'd-flex',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.OWNER,
		text: 'MERCHANT.OWNER',
		additionalClass: 'd-flex',
		claim: CLAIMS.SUPER_ADMIN_AND_MERCHANT_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.CONTACT,
		text: 'MERCHANT.CONTACT_INFO',
		additionalClass: 'd-flex',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.CUSTOMER,
		text: 'MERCHANT.CUSTOMER_INFO',
		additionalClass: 'tab-customer-support',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.DEVELOPER,
		text: 'MERCHANT.DEVELOPER_INFO',
		additionalClass: 'tab-developer',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.BANK,
		text: 'MERCHANT.BANK',
		additionalClass: 'tab-bank',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.FEES,
		text: 'MERCHANT.FEES',
		additionalClass: 'tab-fees',
		claim: CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.FUNDS_RESERVE,
		text: 'MERCHANT.FUNDS_RESERVE',
		additionalClass: 'tab-funds-reserve',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.SITES,
		text: 'MERCHANT.SITES',
		additionalClass: 'tab-sites',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.SPECIAL_TERMS,
		text: 'MERCHANT.SPECIAL_TERMS',
		additionalClass: 'tab-terms',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.CONFIGURATIONS,
		text: 'MERCHANT.CONFIGURATION',
		additionalClass: 'tab-config',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.WORKFLOWS,
		text: 'MERCHANT.WORKFLOWS',
		additionalClass: 'tab-config',
		claim: CLAIMS.SUPER_ADMIN
	},
	{
		path: MERCHANTS_TABS.PAYMENT_GATEWAY,
		text: 'MERCHANT.PAYMENT_GATEWAY',
		additionalClass: 'tab-payment',
		claim: CLAIMS.SUPER_ADMIN
	},
];

const additionalNavigationLinks = [
	{
		path: MERCHANTS_TABS.CUSTOMER,
		text: 'MERCHANT.CUSTOMER_INFO',
		additionalClass: 'tab-customer-support-additional',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.DEVELOPER,
		text: 'MERCHANT.DEVELOPER_INFO',
		additionalClass: 'tab-developer-additional',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.BANK,
		text: 'MERCHANT.BANK',
		additionalClass: 'tab-bank-additional',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.FEES,
		text: 'MERCHANT.FEES',
		additionalClass: 'tab-fees-additional',
		claim: CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.FUNDS_RESERVE,
		text: 'MERCHANT.FUNDS_RESERVE',
		additionalClass: 'tab-funds-reserve-additional',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.SPECIAL_TERMS,
		text: 'MERCHANT.SPECIAL_TERMS',
		additionalClass: 'tab-terms-additional',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.CONFIGURATIONS,
		text: 'MERCHANT.CONFIGURATION',
		additionalClass: 'tab-config-additional',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.WORKFLOWS,
		text: 'MERCHANT.WORKFLOWS',
		additionalClass: 'tab-config-additional',
		claim: CLAIMS.SUPER_ADMIN
	},
	{
		path: MERCHANTS_TABS.SITES,
		text: 'MERCHANT.SITES',
		additionalClass: 'tab-sites-additional',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.PAYMENT_GATEWAY,
		text: 'MERCHANT.PAYMENT_GATEWAY',
		additionalClass: 'tab-payment-additional',
		claim: CLAIMS.SUPER_ADMIN
	},
	{
		path: MERCHANTS_TABS.WEBHOOKS,
		text: 'MERCHANT.WEBHOOKS',
		additionalClass: '',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.API_KEY,
		text: 'MERCHANT.API_KEY',
		additionalClass: '',
		claim: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN
	},
	{
		path: MERCHANTS_TABS.DOCUMENTS,
		text: 'SELF_ONBOARDING.SECTION_TITLE.DOCUMENT',
		additionalClass: '',
		claim: CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN
	},
];

interface IMerchantLayoutProps {
	children: React.ReactElement;
	activate: () => Promise<void>;
	backUrl: string;
}


export const MerchantLayout = (props: IMerchantLayoutProps): JSX.Element => {
	const { children, backUrl, activate } = props;

	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const location = useLocation();
	const navigate = useNavigate();
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);

	const { merchantState: { isMerchantDataLoading, merchantData }} = useMerchants();
	const [localStatus, setLocalStatus] = useState(merchantData?.status === 1);
	const [loading, setLoading] = useState<boolean>(false);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

	const segments = location.pathname.split('/');
	const activeSection = segments[segments.length - 1];


	useEffect(() => {
		if (merchantData?.status) {
			setLocalStatus(merchantData.status === 1);
		}
	}, [merchantData]);

	const changeStatus = async (): Promise<void> => {
		setLoading(true);
		setLocalStatus(!localStatus);
		try {
			await activate();
			setLoading(false);
		} catch (e) {
			setLocalStatus(localStatus);
			setLoading(false);
			console.log('e', e);
		}
	};

	const getState = (): any => {
		const pages = [MERCHANTS_TABS.CHECKOUT_CONFIGURATIONS, MERCHANTS_TABS.WIDGET_CONFIGURATIONS, MERCHANTS_TABS.SENSE_JS, MERCHANTS_TABS.FEES_HISTORY, MERCHANTS_TABS.WEBHOOKS];
		const isPage = pages?.find(el => location?.pathname.includes(el));
		if (isPage) {
			return { ...location?.state };
		} else {
			return null;
		}
	};

	const openModal = (): void => {
		setModalState({
			isOpen: true,
			isRightSideModal: true,
			modalTitle: 'Activity logs',
			contentClassName: 'd-block',
			size: 'md',
			content: <ApplicationDetailActivityLogsModal id={id} />
		});
	};

	return (
		<div>
			<div>
				<div className="mb-5 d-flex justify-content-between align-items-center">
					<GoBackBtn path={backUrl} state={getState()} />

					<ButtonComponent
						buttonType={BUTTON_TYPE.FAKE_LINK}
						buttonName="Activity log"
						additionalClass="btn-sm me-7"
						onButtonClick={openModal}
						withoutUnderline
					/>
				</div>
				<div className="card mb-5">
					<div className="card-body border-bottom d-flex justify-content-between align-items-center">

						<div className="fw-bolder fs-5 fs-sm-3">{merchantData?.legalEntityName}</div>
							<div className="d-flex justify-content-end align-items-center">
								<Toggle
									size="md"
									checked={localStatus}
									className={clsx(localStatus ? 'is-active' : '')}
									onChange={changeStatus}
									disabled={loading || checkRoles(authUser, CLAIMS.INTEGRATION_PARTNER_ADMIN)}
								/>
								<div className="fs-6 fw-bold ms-1">
									{t('MERCHANT.ACTIVE')}
								</div>
							</div>
					</div>
					<div className="d-flex align-items-center justify-content-between merchant-navigation px-9">
						{
							navigationLinks
								?.filter((item) => {
									if (checkRoles(authUser, item?.claim)) {
										return item;
									}
								})
								.map(({ path, text, additionalClass }) => (
									<Link
										key={path}
										to={`/accounts/merchants/${id}/${path}`}
										state={{ ...location.state }}
									className={clsx(
										'align-items-center justify-content-center fs-8 fs-sm-7 fw-bolder text-center px-3 h-70px tab-style',
										additionalClass,
										activeSection === path ? 'text-danger tab-bgc' : 'text-dark')
								}
								>
									{t(text)}
								</Link>
							))
						}
						<Dropdown
							noCaret
							icon={<FontAwesomeIcon icon={faEllipsisVertical}/>}
							placement="bottomEnd"
							className="merchant-hidden-tabs"
						>
							{
								additionalNavigationLinks
									?.filter((item) => {
										if (checkRoles(authUser, item.claim)) {
											return item;
										}
									})
									.map(({ path, text, additionalClass }) => (
									<Dropdown.Item
										key={path}
										onClick={(): void => navigate(
											`/accounts/merchants/${id}/${path}`,
											{ state: { ...location.state }}
										)}
										className={clsx(
											'fw-bolder fs-7 fc-dropdown-item',
											additionalClass,
											activeSection === path ? 'text-danger tab-bgc' : 'text-dark'
										)}
									>
										{t(text)}
									</Dropdown.Item>
								))
							}
						</Dropdown>

					</div>
				</div>
			</div>
			{children}
			{
				!isMerchantDataLoading && (
					<div className="d-flex flex-stack flex-wrap pt-10">
						<ButtonComponent
							buttonType={BUTTON_TYPE.TRANSPARENT}
							buttonName={t('MERCHANT.BACK_BUTTON')}
							onButtonClick={(): void => navigate(backUrl)}
						/>
					</div>
				)
			}

			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>

		</div>
	);
};
